import { uniqWith } from 'es-toolkit';
import React, { useMemo } from 'react';
import { ExtrudeGeometry, Matrix4, Shape, Vector2 } from 'three';

import { Gusset, isTruss } from '@paragon/contract-library';
import { distanceEquals, LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { circularPairwise } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { useClickItem } from '../../store/actions/sceneInteractionActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';
import { Line, LinePoints } from '../util/Line';

const defaultColor = 0xff6666;
const selectedColor = 0xff0000;

// Gussets can be different thicknesses, but just use a standard one here. Gussets have a `material` property that
// contains the thickness, among other things, which is sometimes measured in 32nds of an inch. That fact made
// recording it separately cumbersome, and recording it is of little utility at present anyway.
const gussetThickness = 7 / 16;

interface GussetVisualProps {
  readonly gusset: Gusset;
}

export const GussetVisual = (props: GussetVisualProps) => {
  const { gusset } = props;
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const selectedItem = useAppSelector(getSingleSelectedItemEntryOrNull);

  const selected =
    selectedItem === null ? false : selectedItem.type === 'gusset' && selectedItem.item.guid === gusset.guid;

  const shape = useMemo(() => new Shape(gusset.geometry.map((vertex) => new Vector2(vertex.x, vertex.y))), [gusset]);
  const extrudeGeometry = useMemo(
    () => new ExtrudeGeometry(shape, { depth: gussetThickness, bevelEnabled: false }),
    [shape],
  );
  const gussetPolygon = useMemo(
    () => new Polygon2D(gusset.geometry.map((point) => Point2D.fromObject(point))),
    [gusset],
  );
  const z = useMemo(
    () =>
      activeComponentDesign !== null && isTruss(activeComponentDesign.component)
        ? activeComponentDesign.component.thickness
        : 0,
    [activeComponentDesign],
  );
  const matrix = useMemo(() => new Matrix4().makeTranslation(0, 0, z), [z]);
  const bisectorsForRectangularGusset: { shortSideBisector: LinePoints; longSideBisector: LinePoints } | null =
    useMemo(() => {
      const { edges } = gussetPolygon;
      if (edges.length !== 4) return null;

      const onlyRightAngles = circularPairwise(edges.map((edge) => edge.toLine())).every(([edge1, edge2]) =>
        edge1.direction.isPerpendicularTo(edge2.direction),
      );
      if (!onlyRightAngles) return null;

      const sideLengths = uniqWith(
        edges.map((edge) => edge.length),
        distanceEquals,
      );
      const zPaddedForBisectorLine = z + gussetThickness + 0.1;
      const longerSideLength = Math.max(...sideLengths);
      const longSides = edges.filter((edge) => distanceEquals(edge.length, longerSideLength));
      const longSideBisector = LineSegment2D.fromPointObjects(longSides[0].midpoint, longSides[1].midpoint).to3D(
        zPaddedForBisectorLine,
      );
      const shorterSideLength = Math.min(...sideLengths);
      const shortSides = edges.filter((edge) => distanceEquals(edge.length, shorterSideLength));
      const shortSideBisector = LineSegment2D.fromPointObjects(shortSides[0].midpoint, shortSides[1].midpoint).to3D(
        zPaddedForBisectorLine,
      );
      return {
        longSideBisector: [
          [longSideBisector.basePoint.x, longSideBisector.basePoint.y, longSideBisector.basePoint.z],
          [longSideBisector.endPoint.x, longSideBisector.endPoint.y, longSideBisector.endPoint.z],
        ],
        shortSideBisector: [
          [shortSideBisector.basePoint.x, shortSideBisector.basePoint.y, shortSideBisector.basePoint.z],
          [shortSideBisector.endPoint.x, shortSideBisector.endPoint.y, shortSideBisector.endPoint.z],
        ],
      };
    }, [gussetPolygon, z]);

  const onClick = useClickItem({ type: 'gusset', item: gusset });

  if (activeComponentDesign === null) return null;

  return (
    <group>
      <mesh matrix={matrix} matrixAutoUpdate={false} geometry={extrudeGeometry} onClick={onClick}>
        <meshBasicMaterial transparent opacity={selected ? 1 : 0.75} color={selected ? selectedColor : defaultColor} />
      </mesh>
      <lineSegments matrix={matrix} matrixAutoUpdate={false}>
        <edgesGeometry attach="geometry" args={[extrudeGeometry]} />
        <lineBasicMaterial color="black" linewidth={5} />
      </lineSegments>
      {bisectorsForRectangularGusset !== null && (
        <>
          <Line points={bisectorsForRectangularGusset.longSideBisector} color="#000" dashed />
          <Line points={bisectorsForRectangularGusset.shortSideBisector} color="#000" dashed />
        </>
      )}
    </group>
  );
};
